// Copyright 2018 Sorama B.V.

import { autoinject, bindable, BindingEngine, bindingMode, computedFrom, Disposable } from "aurelia-framework";
import { IExportable } from "libraries/Export/IExportable";
import { SVGNode } from "libraries/SVG/SVGNode";
import { Unit, UnitValue } from "units/Unit";
import { DrawLayer, Margin, Pair, Rect } from "../IPlotPart";
import { PlotPartBase } from "../PlotPartBase";

export interface Annotation {
    x?: UnitValue;
    y?: UnitValue;
    color: string;
    text: string;
    harmonic?: number;
}

interface DrawAnnotation {
    text: string;
    color: string;

    xText: number;
    yText: number;
}

interface HorizontalLineAnnotation extends DrawAnnotation {
    y: number;
}

interface VerticalLineAnnotation extends DrawAnnotation {
    x: number;
    hover?: boolean;
    harmonic?: number;
}

interface PointAnnotation extends DrawAnnotation {
    x: number;
    y: number;
}

@autoinject
export class AnnotationPart extends PlotPartBase implements IExportable {
    private static InstanceCounter = 0;
    private readonly bindingEngine: BindingEngine;

    private instanceNumber: number = AnnotationPart.InstanceCounter++;
    private plotElement: SVGElement;

    @bindable({ defaultBindingBehavior: bindingMode.twoWay })
    private annotations: Annotation[] = [];

    @bindable()
    public color = "white";

    public horizontalLineAnnotations: HorizontalLineAnnotation[] = [];
    public verticalLineAnnotations: VerticalLineAnnotation[] = [];
    public pointAnnotations: PointAnnotation[] = [];

    private valueBackgroundWidth = 50;

    public minimumSpacing = 15;

    @bindable public lowerHarmonicsColor = "blue";
    @bindable public higherHarmonicsColor = "green";

    @computedFrom("actualSize")
    public get viewBox(): string {
        return this.size ? "0 0 " + this.actualSize.width + " " + this.actualSize.height : "0 0 1 1";
    }

    public get clipPathId(): string {
        return "annotationClip" + this.instanceNumber;
    }

    public get gradientId(): string {
        return "annotationGradient" + this.instanceNumber;
    }

    private annotationDisposable: Disposable;

    constructor(bindingEngine: BindingEngine) {
        super(0, DrawLayer.AbovePlot);
        this.bindingEngine = bindingEngine;
    }

    public onSize(size: Rect, margin: Margin): void {
        super.onSize(size, margin);
        this.updateDrawAnnotations();
    }

    public onViewPortComputed(viewPort: Rect, viewPortBounds: Rect, units: Pair<Unit>): void {
        super.onViewPortComputed(viewPort, viewPortBounds, units);
        this.updateDrawAnnotations();
    }

    public annotationsChanged(newValue: Annotation[]): void {
        this.updateDrawAnnotations();
    }

    private annotationsCollectionChanged = (): void => {
        this.updateDrawAnnotations();
    };

    private updateDrawAnnotations(): void {
        if (!this.annotations) {
            this.pointAnnotations = [];
            this.horizontalLineAnnotations = [];
            this.verticalLineAnnotations = [];
            return;
        }

        this.horizontalLineAnnotations = this.annotations
            .filter((anno) => !anno.x)
            .map(this.toHorizontalLineAnnotation);
        this.verticalLineAnnotations = this.annotations.filter((anno) => !anno.y).map(this.toVerticalLineAnnotation);
        this.pointAnnotations = this.annotations.filter((anno) => !!anno.x && !!anno.y).map(this.toPointAnnotation);
    }

    private toHorizontalLineAnnotation = (annotation: Annotation): HorizontalLineAnnotation => {
        return {
            text: annotation.text,
            color: annotation.color,
            y: this.toViewY(annotation.y),

            yText: 0,
            xText: 0,
        };
    };

    private toVerticalLineAnnotation = (annotation: Annotation): VerticalLineAnnotation => {
        return {
            text: annotation.text,
            color: annotation.color,
            x: this.toViewX(annotation.x),
            harmonic: annotation.harmonic,

            yText: 0,
            xText: 0,
        };
    };

    private toPointAnnotation = (annotation: Annotation): PointAnnotation => {
        return {
            text: annotation.text,
            color: annotation.color,
            y: this.toViewY(annotation.y),
            x: this.toViewX(annotation.x),

            yText: 0,
            xText: 0,
        };
    };

    private toViewX(x: UnitValue): number {
        if (!this.sourceUnits || !this.actualSize) return 0;

        const xValue = x.toUnit(this.sourceUnits.x);
        return ((xValue - this.viewPort.x) / this.viewPort.width) * this.actualSize.width;
    }

    private toViewY(y: UnitValue): number {
        if (!this.sourceUnits || !this.actualSize) return 0;

        const yValue = y.toUnit(this.sourceUnits.y);
        return ((yValue - this.viewPort.y) / this.viewPort.height) * this.actualSize.height;
    }

    public exportToSvg(): Promise<SVGNode> {
        if (!this.plotElement) return undefined;

        const svgs = SVGNode.getSvgsInElement(this.plotElement);
        if (svgs.length != 1) return undefined; // Only 1 expected..

        const svg = svgs[0];

        // Find clip path in svg and give it a unique identifier
        const newClipPathId = this.clipPathId + "_preview";
        svg.updateWhere(
            (x) => x.attributes["id"] == this.clipPathId,
            (x) => (x.attributes["id"] = newClipPathId)
        );
        svg.updateWhere(
            (x) => Object.prototype.hasOwnProperty.call(x.attributes, "clip-path"),
            (x) => (x.attributes["clip-path"] = "url(#" + newClipPathId + ")")
        );

        // Find annotation id and make it unique for the export
        this.verticalLineAnnotations.forEach((x, i) => {
            const newGradientId = this.gradientId + "_" + i + "_preview";
            svg.updateWhere(
                (x) => x.attributes["id"] == this.gradientId + "_" + i,
                (x) => (x.attributes["id"] = newGradientId)
            );
            svg.updateWhere(
                (x) => x.attributes["stroke"] && x.attributes["stroke"].indexOf(this.gradientId + "_" + i) != -1,
                (x) => (x.attributes["stroke"] = "url(#" + newGradientId + ")")
            );
        });

        return Promise.resolve(svg);
    }

    private computeSelectedValueX(selectedLine: VerticalLineAnnotation): number {
        let start = -this.valueBackgroundWidth / 2;
        let end = this.valueBackgroundWidth / 2;

        const width: number = 35 + 2 * selectedLine.text.length;

        // Prevent fliepeltje from going over the left
        if (selectedLine.x + start < 0) {
            start = -selectedLine.x;
            end = start + width;
        }

        // Prevent fliepeltje from going over the right
        if (selectedLine.x + end > this.actualSize.width) {
            end = this.actualSize.width - selectedLine.x;
            start = end - width;
        }

        return start + (end - start) / 2;
    }

    private computeSelectedValueBackgroundPath(selectedLine: VerticalLineAnnotation): string {
        const width: number = 35 + 2 * selectedLine.text.length;

        let start = -this.valueBackgroundWidth / 2;
        let end = this.valueBackgroundWidth / 2;

        const arrowSize = 10;
        let arrowStart = -arrowSize / 2;
        let arrowEnd = arrowSize / 2;

        // Prevent fliepeltje from going over the left
        if (selectedLine.x + start < 0) {
            start = -selectedLine.x;
            end = start + width;
        }

        // Prevent fliepeltje from going over the right
        if (selectedLine.x + end > this.actualSize.width) {
            end = this.actualSize.width - selectedLine.x;
            start = end - width;
        }

        // Prevent arrow from going over the left
        if (selectedLine.x + arrowStart < 0) {
            arrowStart = -selectedLine.x;
            arrowEnd = arrowStart + arrowSize;
        }

        // Prevent arrow from going over the right
        if (selectedLine.x + arrowEnd > this.actualSize.width) {
            arrowEnd = this.actualSize.width - selectedLine.x;
            arrowStart = arrowEnd - arrowSize;
        }

        return (
            `${start} 0, ` +
            `${start} 15, ` +
            `${arrowStart} 15, ` +
            "0 20, " +
            `${arrowEnd} 15, ` +
            `${end} 15, ` +
            `${end} 0 `
        );
    }

    public doMouseover(index: number): void {
        this.verticalLineAnnotations[index].hover = true;
    }

    public doMouseout(index: number): void {
        this.verticalLineAnnotations[index].hover = false;
    }
}
